import canAutoPlay from "can-autoplay"
import * as Phaser from "phaser-ce"
import { AppRoutes } from "../../common/AppsRouter"
import { CCUtils } from "../../common/CCUtils"
import { COLOR } from "../../common/Color"
import {
    BIRTHDAY_DAY,
    BIRTHDAY_MONTH,
    LOGIN_ID,
} from "../../common/LocalConfig"
import { log } from "../../common/Logging"
import { FontType } from "../../common/TextUtils"
import { TimeUtils } from "../../common/TimeUtils"
import { AuthStoreSA } from "../../react/login/AuthStoreSA"
import { StudentStoreSA } from "../../react/students/StudentStoreSA"
import { Rectangle } from "../generic-sprites/Shape"
import { colorNum, ColorUtils } from "./ColorUtils"
import { PhaserText } from "./PhaserText"
import { Scale } from "./Scale"

export class PhaserUtils {
    /**
     * Wraps the phaser loader to do the things we always need to do and simplify it for our use case.
     */
    static makeLoader = (game: Phaser.Game, location: string) => {
        const loaders = {
            spritesheet: (
                assetName: string,
                fileName: string,
                width: number,
                height: number,
            ) =>
                game.load.spritesheet(
                    assetName,
                    CCUtils.asset(`${location}/sprites/${fileName}`),
                    width,
                    height,
                ),
            audio: (
                assetName: string,
                fileName: string,
                gendered?: boolean,
            ) => {
                const modFileName = gendered
                    ? CCUtils.genderifyFileName(fileName)
                    : fileName
                return game.load.audio(
                    assetName,
                    CCUtils.asset(`${location}/audio/${modFileName}`),
                )
            },
            video: (
                assetName: string,
                fileName: string,
                gendered?: boolean,
            ) => {
                const modFileName = gendered
                    ? CCUtils.genderifyFileName(fileName)
                    : fileName
                return game.load.video(
                    assetName,
                    CCUtils.asset(`${location}/video/${modFileName}`),
                )
            },
            atlas: (assetName: string, fileName: string) =>
                game.load.atlas(
                    assetName,
                    CCUtils.asset(
                        `${location}/atlases/${fileName.substring(
                            0,
                            fileName.length - 5,
                        )}.png`,
                    ),
                    CCUtils.asset(`${location}/atlases/${fileName}`),
                ),
            image: (assetName: string, fileName: string) =>
                game.load.image(
                    assetName,
                    CCUtils.asset(`${location}/images/${fileName}`),
                ),
        }

        return {
            ...loaders,
            autoLoad: ({
                imageEnum = {},
                audioEnum = {},
                audioGenderedEnum = {},
                atlasEnum = {},
                videoEnum = {},
                videoGenderedEnum = {},
            }) => {
                CCUtils.enumValues(imageEnum).forEach((assetPath: string) => {
                    loaders.image(assetPath, assetPath)
                })
                CCUtils.enumValues(audioEnum).forEach((assetPath: string) => {
                    loaders.audio(assetPath, assetPath)
                })
                CCUtils.enumValues(audioGenderedEnum).forEach(
                    (assetPath: string) => {
                        loaders.audio(assetPath, assetPath, true)
                    },
                )
                CCUtils.enumValues(atlasEnum).forEach((assetPath: string) => {
                    loaders.atlas(assetPath, assetPath)
                })
                CCUtils.enumValues(videoEnum).forEach((assetPath: string) => {
                    loaders.video(assetPath, assetPath)
                })
                CCUtils.enumValues(videoGenderedEnum).forEach(
                    (assetPath: string) => {
                        loaders.video(assetPath, assetPath, true)
                    },
                )
            },
        }
    }

    /**
     * Should be called in every game. Sets background COLOR as default yellow, so call separate functions for
     * different background colors, or just set the game's stage and document's body's backgroundColors separately
     * if you need them to be different.
     *
     * @param {Phaser.Game} game
     */
    static sharedCreateFunctions = async (game: Phaser.Game) => {
        if (
            CCUtils.debug &&
            StudentStoreSA.instance.student == null &&
            LOGIN_ID
        ) {
            log.debug(
                "Since this is debug, the student is null, and we have a local config login id, let's login for you as " +
                    LOGIN_ID,
            )
            await AuthStoreSA.instance.loginStudent(
                LOGIN_ID,
                TimeUtils.birthMonthAndDayToBirthCode(
                    BIRTHDAY_MONTH,
                    BIRTHDAY_DAY,
                ),
            )
        }
        if (StudentStoreSA.instance.student == null) {
            log.warn(
                "Tried to enter a phaser activity, but student is undefined.",
            )
            window.location.replace(AppRoutes.home)
            return
        }
        Scale.resizeScreen(game, false)
        ColorUtils.setBackgroundColor(game, COLOR.yellow)
        game.stage.disableVisibilityChange = true

        // The default stopAll in Phaser can blow up if a sound has an onStop, so instead we iterate the private sounds
        // ourselves and remove all the event listeners in addition to stopping them.
        // tslint:disable-next-line
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const soundManager = game.sound as any
        for (const audio of soundManager._sounds) {
            audio.onPlay.removeAll()
            audio.onStop.removeAll()
            audio.stop()
        }

        if (CCUtils.debug) {
            game.time.advancedTiming = true
            const fpsPrinter = game.time.create()
            const fps = PhaserText.create(game, "Loading...", {
                x: Scale.width() * 0.01,
                y: Scale.width() * 0.01,
                fontPx: 10,
                type: FontType.MONOSPACE,
                color: COLOR.green,
            })
            fpsPrinter.loop(1000, () => {
                fps.text =
                    PhaserUtils.fps(game) + " " + PhaserUtils.cursorCoors(game)
                fps.bringToTop()
            })
            fpsPrinter.add(1000, () => {
                game.time.fpsMin = 60
            })
            fpsPrinter.start()
        }
        await PhaserUtils.waitForMobileTap(game)
    }

    static waitForMobileTap = async (game: Phaser.Game) => {
        const can = await canAutoPlay.audio()
        if (!can.result) {
            log.debug(can)
            const group = game.add.group()
            new Rectangle(
                game,
                0,
                0,
                Scale.availableWidth(),
                Scale.availableHeight(),
                {
                    fillColor: colorNum.black,
                    fillAlpha: 0.3,
                    group,
                },
            )
            PhaserText.create(game, "Tap to start!", {
                color: COLOR.white,
                centerInWorldXOnly: true,
                y: Scale.y(0.45),
                group,
            })
            return new Promise<void>((resolve) => {
                game.paused = true
                game.input.onTap.addOnce(() => {
                    game.paused = false
                    group.destroy(true)
                    resolve()
                })
            })
        }
    }

    static coors = (x: number, y: number, places = 100) => {
        const scaleX =
            Math.round(
                ((x - Scale.cachedValues.widthOffset) * places) / Scale.width(),
            ) / places
        const scaleY =
            Math.round(
                ((y - Scale.cachedValues.heightOffset) * places) /
                    Scale.height(),
            ) / places
        return (
            `Scale.x(${scaleX}), Scale.y(${scaleY})\n` +
            `x: Scale.x(${scaleX}), y: Scale.y(${scaleY})`
        )
    }

    static loggedIn = (): boolean => !!StudentStoreSA.instance.student

    static notLoggedInMessage = (game: Phaser.Game) =>
        PhaserText.create(game, "You must log in to view this activity.", {
            x: Scale.x(0.5),
            y: Scale.y(0.5),
            fontPx: 10,
            type: FontType.MONOSPACE,
            color: COLOR.red,
        })

    static disableGroup = (group: Phaser.Group) =>
        group.forEach((child) => (child.inputEnabled = false))

    static enableGroup = (group: Phaser.Group) =>
        group.forEach((child) => (child.inputEnabled = true))

    private static fps = (game: Phaser.Game) =>
        `FPS: ${game.time.fps} Min: ${game.time.fpsMin} Max: ${game.time.fpsMax}`

    private static cursorCoors = (game: Phaser.Game) =>
        PhaserUtils.coors(game.input.x, game.input.y)
}
